import { useFetchSSOTokens, useSCIMTokenDetails } from '@common/SCIM/hooks';
import { theme } from '@prophecy/theme';
import { Button, Separator, Spinner, Stack, StackItem } from '@prophecy/ui-v3';
import { message, toast } from '@prophecy/ui-v3/ui-compat';
import { useAfterNextRender, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useMutation as useReactMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { getAllIdpConfig, setConfigureSSO, setIdpConfig, setSSO } from '../../../data/apis/api';
import { AuthTypes, ProphecyManagedData, ProviderType } from '../../../data/apis/types';
import { AuthConfig, IdpConfig } from '../config/admin';
import AuthProvider from './AuthProvider/AuthProvider';
import { AdminData, LDAPData, SAMLData, UserMatchers, UserMatcherTypeWithIndex } from './AuthProvider/types';

const StyledSpinner = styled(Spinner)`
  height: 100%;
`;
const StyledButton = styled(Button)`
  width: 100px;
` as unknown as typeof Button;
const validateUserAttribute = (data: UserMatcherTypeWithIndex[]) => {
  let errorMsg = '';
  let userMatchers: UserMatchers[] = [];
  for (let i = 0; i < data.length; i++) {
    const { key: _key, __path, ...rest } = data[i];
    if (!rest.userAttr) {
      errorMsg = `User attribute is required for row ${i + 1}`;
      break;
    } else if (!rest.groupAttr) {
      errorMsg = `Group attribute is required for row ${i + 1}`;
      break;
    }
    if (!errorMsg) {
      const matchFoundIndex = userMatchers.findIndex((match) => match.userAttr === rest.userAttr);
      if (matchFoundIndex > -1) {
        errorMsg = `Duplicate user attribute for row ${i + 1}`;
        break;
      }
    }
    userMatchers.push(rest);
  }
  return { errorMsg, userMatchers };
};

export default function SSO() {
  const queryClient = useQueryClient();

  const { isAzureSCIMEnabled, isSCIMGlobalVariableSet, isSCIMV1, isSCIMEnabled, defaultAuthProvider, email, isSaas } =
    useSCIMTokenDetails();

  const { afterNextRender } = useAfterNextRender();

  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors, isValid }
  } = useForm<AdminData>({
    mode: 'all',
    defaultValues: {
      authProvider: defaultAuthProvider,
      idp: ProviderType.okta,
      [AuthTypes.ldap]: {
        userSearch: {
          username: 'mail'
        }
      }
    }
  });

  const providerType = watch('authProvider');

  const userAttributeTableControl = useRef<UserMatcherTypeWithIndex[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const ssoConfigQueryKey = ['ssoConfig', email];

  if (isAzureSCIMEnabled) {
    ssoConfigQueryKey.push(providerType);
  }

  const getAuthProvider = useCallback(
    (type: AuthTypes) => {
      if (type === AuthTypes.azuread && isSaas) {
        return AuthTypes.saml;
      }
      return type;
    },
    [isSaas]
  );

  const saveMutation = useReactMutation<{ message?: string; success: boolean; data: AdminData }, AdminData, AdminData>(
    ['ConfigureSS'],
    async () => {
      let resp;
      const type = providerType;
      const idp = getValues('idp');
      const idpconfig = (getValues(type as keyof AdminData) || {}) as ProphecyManagedData;
      const orgID = getValues('orgID');
      const orgWideTeamName = getValues('orgWideTeamName');
      if (isProphecyManaged && isSCIMEnabled) {
        resp = await setConfigureSSO({ identifier: email, orgID, orgWideTeamName });
        resp.data = resp;
        queryClient.invalidateQueries(ssoConfigQueryKey);
      } else if (
        (type === AuthTypes.saml && isSCIMGlobalVariableSet) ||
        (type === AuthTypes.azuread && isAzureSCIMEnabled)
      ) {
        // azure doesn't have options to select provider type, hardcoding to azure
        const _idp = providerType === AuthTypes.azuread && isAzureSCIMEnabled ? ProviderType.azuread : idp;
        resp = await setSSO({
          type,
          idpconfig,
          idp: _idp,
          orgID,
          orgWideTeamName
        });
        resp.data = resp;
      } else {
        resp = await setIdpConfig({
          type,
          idp,
          identifier: email,
          idpconfig
        });
      }
      return resp;
    },
    {
      onError: () => {
        message.error({ content: 'Authentication data update failed.' });
      }
    }
  );

  const [scimTokens, setScimTokens] = useState<Record<string, string>>({});
  // Saved state, not form state: generating a certificate hits the server straight away.
  const [savedSigning, setSavedSigning] = useState({ connectorId: '', sloEnabled: false, hasLogoutSigningKey: false });

  const handleSSOData = usePersistentCallback((data: ProphecyManagedData) => {
    setValue('orgID', data.orgID);
    if (data.idp) {
      setValue('idp', data.idp);
    }
    setValue('orgWideTeamName', data.orgWideTeamName);
    // oxlint-disable-next-line no-unused-expressions
    data.scimToken &&
      setScimTokens((prevState) => ({
        ...prevState,
        [data.type]: data.scimToken
      }));
    if (data.type) {
      const authType = getAuthProvider(data.type);
      setValue('authProvider', authType, { shouldValidate: true });
      if (data.idpconfig) {
        setValue(authType, data.idpconfig as IdpConfig, { shouldValidate: true });
      }
    }
  });

  const { prophecyManagedData, isOktaSettingsFound, isSSOConfigLoading } = useFetchSSOTokens(
    email,
    isAzureSCIMEnabled,
    providerType as AuthTypes,
    handleSSOData
  );

  useEffect(() => {
    const scimToken = scimTokens[providerType] || '';
    setValue('scimToken', scimToken, { shouldValidate: true });
  }, [providerType, setValue, scimTokens]);

  const hasSCIMToken = Boolean(getValues('scimToken'));
  const isOktaConfigured = Boolean(isSCIMV1 || (getValues('orgID') && getValues('orgWideTeamName'))) && hasSCIMToken;
  const userType = prophecyManagedData?.type;

  const setAuthProviderData = usePersistentCallback(async () => {
    const { data } = await getAllIdpConfig();
    const configs: AuthConfig[] = data?.config || [];
    configs.forEach((config, i) => {
      const isSaml = config.type === AuthTypes.saml;
      const isProphecy = config.type === AuthTypes.prophecymanaged;
      const scimToken = getValues('scimToken');
      if (i === 0 && !scimToken) {
        setValue('authProvider', getAuthProvider(userType || config.type), { shouldValidate: true });
      }
      if (isSaml) {
        setValue('idp', config.idp || ProviderType.okta, { shouldValidate: true });
        setSavedSigning({
          connectorId: config.id,
          sloEnabled: Boolean((config.idpConfig as SAMLData)?.sloEnabled),
          hasLogoutSigningKey: Boolean(config.hasLogoutSigningKey)
        });
      }
      const idpConfig = config.idpConfig as IdpConfig;
      if (config.type === AuthTypes.ldap) {
        userAttributeTableControl.current = ((idpConfig as LDAPData)?.groupSearch?.userMatchers ||
          []) as UserMatcherTypeWithIndex[];
      }
      if ((!isSaml && !isProphecy && isSCIMEnabled) || isAzureSCIMEnabled || !isSCIMEnabled) {
        setValue(config.type, idpConfig, { shouldValidate: true });
      }
    });
    afterNextRender(() => setIsLoading(false));
  });

  // Only the signing state: setAuthProviderData rewrites the whole form, discarding unsaved edits.
  const refreshSigningState = usePersistentCallback(async () => {
    try {
      const { data } = await getAllIdpConfig();
      const samlConfig = ((data?.config || []) as AuthConfig[]).find((config) => config.type === AuthTypes.saml);
      setSavedSigning({
        connectorId: samlConfig?.id || '',
        sloEnabled: Boolean((samlConfig?.idpConfig as SAMLData)?.sloEnabled),
        hasLogoutSigningKey: Boolean(samlConfig?.hasLogoutSigningKey)
      });
    } catch {
      /* the save or certificate already succeeded; the next load shows the saved state */
    }
  });

  useEffect(() => {
    if (!isLoading) {
      return;
    }
    if (!isSSOConfigLoading) {
      setIsLoading(false);
      return;
    }
    // is v1 and non-sass , fetch based on oktaSetting and  isScimEnabled
    // else fetch once
    if (!isAzureSCIMEnabled || isOktaSettingsFound || !isSCIMEnabled) {
      setAuthProviderData();
    }
  }, [setAuthProviderData, isOktaSettingsFound, isSSOConfigLoading, isLoading, isAzureSCIMEnabled, isSCIMEnabled]);

  const isProphecyManaged = providerType === AuthTypes.prophecymanaged;
  const onSave = async () => {
    const formData = getValues();
    if (formData.authProvider === AuthTypes.ldap) {
      const { errorMsg, userMatchers } = validateUserAttribute(userAttributeTableControl.current);
      if (errorMsg) {
        toast.error({ content: errorMsg });
        return false;
      }
      const ldapData = {
        ...formData.ldap,
        groupSearch: { ...formData.ldap.groupSearch, userMatchers }
      };
      setValue(AuthTypes.ldap, ldapData);
    } else if (formData.authProvider === AuthTypes.azuread) {
      if (formData.azuread.IsUsingManagedIdentity) {
        formData.azuread.clientSecret = '';
      }
    }
    await saveMutation.mutateAsync(formData, {
      onSuccess: (resp) => {
        if (resp.data) {
          handleSSOData(resp.data);
        }

        // change to new default provider once scim is generated
        if (isProphecyManaged && isSCIMEnabled && resp.data.scimToken) {
          setValue('authProvider', getAuthProvider(AuthTypes.azuread));
        }

        if (resp.success) {
          refreshSigningState();
          message.success({ content: resp.message || 'Authentication data updated successfully.' });
        } else {
          message.error({ content: resp.message || 'Authentication data update failed.' });
        }
      }
    });
  };

  return (
    <StyledSpinner spinning={isLoading}>
      <Stack align='space-between' width='100%' height='100%' gap={theme.spaces.x8}>
        {!isLoading && (
          <>
            <StackItem grow='1'>
              <AuthProvider
                updateUserMatchersData={(data) => (userAttributeTableControl.current = data)}
                control={control}
                errors={errors}
                watch={watch}
                getValues={getValues}
                isSaasWithSCIM={isSaas && isSCIMEnabled}
                isOktaConfigured={isOktaConfigured}
                isSCIMV1={isSCIMV1}
                isSCIMEnabled={isSCIMEnabled}
                hasSCIMToken={hasSCIMToken}
                savedSloEnabled={savedSigning.sloEnabled}
                signingConnectorId={savedSigning.connectorId}
                hasLogoutSigningKey={savedSigning.hasLogoutSigningKey}
                refreshSigningState={refreshSigningState}
                isAzureSCIMEnabled={isAzureSCIMEnabled}
              />
            </StackItem>
            <Stack align='end' width='100%' gap={theme.spaces.x24}>
              <Separator />
              <StyledButton
                variant='primary'
                onClick={onSave}
                disabled={
                  saveMutation.isLoading ||
                  !isValid ||
                  (isProphecyManaged && !isSCIMEnabled) ||
                  (isProphecyManaged && isOktaConfigured && !userType && isSCIMEnabled)
                }
                data-test-id={`user-settings-sso-${isProphecyManaged ? 'configure' : 'save'}-button`}>
                {isProphecyManaged ? 'Configure' : 'Save'}
              </StyledButton>
            </Stack>
          </>
        )}
      </Stack>
    </StyledSpinner>
  );
}
